import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ActionBar,
	AddIcon,
	cn,
	Dialog,
	Link,
	ModuleHeader,
	QuestionTemplateIcon,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import type { PermissionTemplate, PermissionTemplateSearchInput } from './types'

export function PermissionTemplatesPage() {
	const { organizationId } = useParams<{ organizationId: string }>()
	const navigate = useNavigate()
	const toast = useToast()
	const [selectedItems, setSelectedItems] = useState<PermissionTemplate[]>([])
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const queryClient = useQueryClient()

	const {
		data: organization,
		isLoading,
		isError,
	} = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})

	const deleteMutation = useMutation({
		...trpc.admin.permissionTemplates.delete.mutationOptions({
			onSuccess: () => {
				setIsDeleteModalOpen(false)
				setSelectedItems([])
				toast.showToast('Successfully deleted!')
			},
			onError: (error) => {
				const message = error.message || 'Something went wrong, please try again'
				toast.showToast(message, 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.permissionTemplates.search.queryKey({
						organizationId: organizationId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const handleDelete = () => {
		deleteMutation.mutate({
			organizationId: organizationId as string,
			ids: selectedItems.map((item) => item.id),
		})
	}

	const columns: Column<PermissionTemplate>[] = [
		{
			field: 'name',
			label: 'Template Name',
			colSpan: 2,
		},
	]

	if (!organizationId) {
		return <Navigate to='/organizations' replace />
	}

	if (isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	if (isError || !organization) {
		return (
			<main className='p-6'>
				<Text>Error loading users. Please try again.</Text>
				<Link href='/organizations' variant='default'>
					Back to Organizations
				</Link>
			</main>
		)
	}

	return (
		<main>
			<div className={cn('p-6', selectedItems.length > 0 && 'pb-26')}>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Organizations', to: '/organizations' },
						{ label: organization.name },
						{ label: 'Permission Templates' },
					]}
					title='Permission Templates'
					logo={{
						url: organization.logo,
						name: organization.name,
					}}
					buttons={[]}
				/>
				<div className='mt-6'>
					<GenericTable<PermissionTemplate, PermissionTemplateSearchInput>
						columns={columns}
						idField='id'
						selectable
						showPagination
						onSelectionChange={(selectedItems) => setSelectedItems(selectedItems)}
						onRowClick={(permission) =>
							navigate(`/organizations/${organizationId}/permission-templates/${permission.id}`)
						}
						actionButtons={[
							{
								icon: AddIcon,
								iconPosition: 'left',
								children: 'Add new template',
								variant: 'primary',
								onClick: () => navigate(`/organizations/${organizationId}/permission-templates/create`),
							},
						]}
						queryOptionsGenerator={(options) =>
							trpc.admin.permissionTemplates.search.queryOptions({
								...options,
								organizationId,
							})
						}
						emptyState={{
							icon: QuestionTemplateIcon,
							title: 'No permission templates to display',
							description: 'Get started by creating a new template',
							actionLabel: 'Create Template',
							onAction: () => navigate(`/organizations/${organizationId}/permission-templates/create`),
						}}
					/>
				</div>

				<Dialog
					isOpen={isDeleteModalOpen}
					onClose={() => setIsDeleteModalOpen(false)}
					onConfirm={handleDelete}
					title='Delete templates'
					message={`You are about to delete ${selectedItems.length} ${selectedItems.length === 1 ? 'template' : 'templates'}. This action cannot be reversed.`}
					type='delete'
					confirmLabel='Delete'
					requireConfirmText
				/>
				{selectedItems.length > 0 && (
					<ActionBar
						rightActions={[
							{
								label: `Delete ${selectedItems.length > 1 ? `(${selectedItems.length})` : ''}`,
								variant: 'destructive',
								onClick: () => setIsDeleteModalOpen(true),
							},
						]}
					/>
				)}
			</div>
		</main>
	)
}
